import {
  CircleCheckIcon,
  InfoIcon,
  Loader2Icon,
  OctagonXIcon,
  TriangleAlertIcon,
} from "lucide-react";
import { type CSSProperties, useEffect, useSyncExternalStore } from "react";
import { Toaster as Sonner, type ToasterProps } from "sonner";

import { useUserPreferences } from "~/features/userPreferences";

type ToastPosition = NonNullable<ToasterProps["position"]>;

// Corners claimed by mounted surfaces, latest last. A full-screen editor whose own chrome sits
// where the toasts land claims another corner for as long as it is open.
let claimedPositions: readonly ToastPosition[] = [];
const positionListeners = new Set<() => void>();

const subscribePosition = (listener: () => void) => {
  positionListeners.add(listener);

  return () => {
    positionListeners.delete(listener);
  };
};

const claimedPosition = (): ToastPosition | undefined => claimedPositions.at(-1);

const setClaimedPositions = (next: readonly ToastPosition[]) => {
  claimedPositions = next;

  for (const listener of positionListeners) {
    listener();
  }
};

/**
 * Sends the app's toasts to `position` while the calling component is mounted. Sonner keeps a
 * toast up while the pointer rests on it, so a toast over the button that raised it never leaves.
 */
const useToastPosition = (position: ToastPosition) => {
  useEffect(() => {
    setClaimedPositions([...claimedPositions, position]);

    // One entry, not every equal one: another surface may hold the same corner.
    return () => {
      const index = claimedPositions.lastIndexOf(position);

      setClaimedPositions(claimedPositions.filter((_, at) => at !== index));
    };
  }, [position]);
};

const Toaster = ({ position, ...props }: ToasterProps) => {
  // shadcn's template reads next-themes; this app's theme lives in the
  // user-preferences feature, so the wrapper adapts here (the one intended
  // customization point of this component).
  const { isDark } = useUserPreferences();
  const claimed = useSyncExternalStore(subscribePosition, claimedPosition, claimedPosition);

  return (
    <Sonner
      theme={isDark ? "dark" : "light"}
      className="toaster group"
      icons={{
        success: <CircleCheckIcon className="size-4" />,
        info: <InfoIcon className="size-4" />,
        warning: <TriangleAlertIcon className="size-4" />,
        error: <OctagonXIcon className="size-4" />,
        loading: <Loader2Icon className="size-4 animate-spin" />,
      }}
      style={
        {
          "--normal-bg": "var(--color-popover)",
          "--normal-text": "var(--color-popover-foreground)",
          "--normal-border": "var(--color-border)",
          "--border-radius": "var(--radius)",
        } as CSSProperties
      }
      toastOptions={{
        classNames: {
          toast: "cn-toast",
        },
      }}
      {...props}
      position={claimed ?? position}
    />
  );
};

export { Toaster, useToastPosition };
